import { ReactNode, useState } from "react";
import { Plus } from "lucide-react";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzSummaryCreateItemMutation } from "@/services/backend/htz/summary/api/summary-api";
import { useHtzReportListQuery } from "@/services/backend/htz/report/api/report-api";
import { Report } from "@/services/backend/htz/report/types/report";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { formatDate } from "@/shared/lib/utilities/date";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";

/**
 * SummaryItemCreateDialog adds an item to a summary by picking one of the
 * existing reports of the process.
 *
 * Reports that are still editable can be picked as well. The values copied
 * onto the item may then drift away from the report; "Aus Anlagenbericht
 * aktualisieren" in the items table refreshes them. Reports the summary
 * already contains cannot be picked again.
 */
export function SummaryItemCreateDialog({
  summaryId,
  processId,
  usedReportIds,
  trigger,
  onCreated,
  disabled,
}: {
  summaryId: UUID;
  processId: UUID;
  usedReportIds: UUID[];
  trigger?: ReactNode;
  onCreated?: (itemId: UUID) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reportId, setReportId] = useState<UUID>("");
  const [createItem, { isLoading, error, reset }] =
    useHtzSummaryCreateItemMutation();
  const { resetField } = useErrValidationFailedContext();

  const { data: reports, error: reportsError } = useHtzReportListQuery({
    process: { active: true, values: [processId] },
    orderBy: [{ field: "date" }],
    limit: 100,
  });

  const selectable = (report: Report) => !usedReportIds.includes(report.id);

  const doCreate = () => {
    if (isLoading || reportId === "") {
      return;
    }

    const itemId = v4();
    // A negative order appends the item behind the existing ones.
    createItem({ id: summaryId, itemId, reportId, order: -1 })
      .unwrap()
      .then(() => {
        toast.success(t("Anlagenbericht hinzugefügt"));
        // "items" holds the error of a summary without items.
        resetField("items");
        setReportId("");
        reset();
        setOpen(false);
        onCreated?.(itemId);
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger || (
        <CommonDialogTrigger
          icon={Plus}
          label={t("Bestehenden hinzufügen")}
          variant="outline"
          size="sm"
          disabled={disabled}
        />
      )}
      <DialogContent>
        <CommonDialogHeader
          title={t("Anlagenbericht hinzufügen")}
          description={t(
            "Der Anlagenbericht wird in die Übersicht des Inspektionsberichts übernommen. Die Werte können danach angepasst werden.",
          )}
        />
        <Field>
          <FieldLabel>{t("Anlagenbericht")}</FieldLabel>
          <Select value={reportId} onValueChange={setReportId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("Bitte wählen")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t("Anlagenberichte")}</SelectLabel>
                {reports?.items.map((report) => (
                  <SelectItem
                    key={report.id}
                    value={report.id}
                    disabled={!selectable(report)}
                  >
                    <span className="flex items-center gap-2">
                      <ReferenceNumber
                        referenceNumber={report.referenceNumber}
                      />
                      {report.date ? ` · ${formatDate(report.date)}` : ""}
                      <DocumentStateBadge document={report} />
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FieldDescription>
            {t("Bereits übernommene Anlagenberichte sind nicht auswählbar.")}
          </FieldDescription>
        </Field>
        {reportsError ? <RTKQueryErrorAlert error={reportsError} /> : null}
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doCreate}
          actionLabel={t("Hinzufügen")}
          disabled={reportId === ""}
        />
      </DialogContent>
    </Dialog>
  );
}
